import axios, { AxiosInstance } from 'axios';
import { SipsAutomationPreferences, SipsAutomationPreferencesResponse } from '../types/automationPreferences';

const API_BASE_URL = import.meta.env.VITE_PLANSCOUT_API_URL || 'http://localhost:8080';

/**
 * Create axios instance with authorization header
 * @param token - Auth0 access token
 */
const createAuthorizedClient = (token: string): AxiosInstance => {
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

/**
 * Fetch SIPS automation preferences for a customer
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 * @returns SipsAutomationPreferences
 */
export const getSipsAutomationPreferences = async (
  token: string,
  customerId: string
): Promise<SipsAutomationPreferences> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get<SipsAutomationPreferencesResponse>(
      `/api/sips/customer/${customerId}/automation-preferences`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch automation preferences');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error fetching SIPS automation preferences for ${customerId}:`, error);
    throw error;
  }
};

/**
 * Update SIPS automation preferences for a customer
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 * @param updates - Partial automation preferences to update
 * @returns Updated SipsAutomationPreferences
 */
export const updateSipsAutomationPreferences = async (
  token: string,
  customerId: string,
  updates: Partial<SipsAutomationPreferences>
): Promise<SipsAutomationPreferences> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.put<SipsAutomationPreferencesResponse>(
      `/api/sips/customer/${customerId}/automation-preferences`,
      updates
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to update automation preferences');
    }

    return response.data.data;
  } catch (error) {
    console.error(`Error updating SIPS automation preferences for ${customerId}:`, error);
    throw error;
  }
};
